import React from "react";
import propTypes from "prop-types";
import classNames from "classnames";

const PLAYING = "playing",
  PAUSED = "paused",
  STOPPED = "stopped";

class Video extends React.Component {
  constructor(props) {
    super(props);
    if (props.controlFunctions) {
      props.controlFunctions.setVolume = this.setVolume;
      props.controlFunctions.setMuted = this.setMuted;
    }
  }
  state = {
    playState: STOPPED,
    showControls: true,
    muted: this.props.muted,
    volume: this.props.volume
  };
  video = this.props.ref || this.props.innerRef || React.createRef();
  render() {
    const {
      nativeControls,
      autoPlay,
      playsInline,
      loop,
      classes,
      videos,
      forceMuted,
      screenTop,
      screenMiddle,
      screenBottom,
      background
    } = this.props;
    const { showControls, muted } = this.state;
    return (
      <React.Fragment>
        <div
          className={classNames(
            classes.PlayerWrapper,
            background && classes.BackgroundVideo
          )}
          style={{
            paddingBottom: `${(100 / videos[0].width) * videos[0].height}%`,
            paddingRight: `${(100 / videos[0].height) * videos[0].width}vh`
          }}
        >
          <div className={classes.VideoWrapper}>
            <video
              ref={this.video}
              className={classes.Video}
              muted={forceMuted || muted}
              autoPlay={autoPlay}
              playsInline={playsInline}
              loop={loop}
              onPlay={this.onPlay}
              onPause={this.onPause}
              onEnded={this.onEnded}
              controls={nativeControls}
            >
              {videos.map(item => {
                if (item.type === "video")
                  return (
                    <source
                      key={item.id}
                      src={item.url}
                      type={item.mime_type}
                    />
                  );
                if (item.type === "image")
                  return (
                    <img
                      key={item.id}
                      src={item.url || item.source_url}
                      alt={item.alt}
                    />
                  );
                return null;
              })}
            </video>
          </div>
        </div>
        <div
          className={classNames(
            classes.ControlsWrapper,
            showControls && classes.ShowControls
          )}
          onClick={this.togglePlay}
          onMouseMove={this.displayControls}
          onMouseLeave={this.hideControls}
        >
          <div className={classes.Controls}>
            <div className={classes.ControlsTop}>
              {screenTop && typeof screenTop === "function"
                ? screenTop(this)
                : screenTop
                ? screenTop
                : null}
            </div>
            <div className={classes.ControlsMiddle}>
              {screenMiddle && typeof screenMiddle === "function"
                ? screenMiddle(this)
                : screenMiddle
                ? screenMiddle
                : null}
            </div>
            <div className={classes.ControlsBottom}>
              {screenBottom && typeof screenBottom === "function"
                ? screenBottom(this)
                : screenBottom
                ? screenBottom
                : null}
            </div>
          </div>
        </div>
        {this.state.playState === STOPPED && videos[0].sizes && (
          <div
            className={classNames(classes.Placeholder)}
            style={{
              backgroundImage: `url(${videos[0].sizes.large})`
            }}
          />
        )}
      </React.Fragment>
    );
  }
  togglePlay = () => {
    this.video.current[this.state.playState === PLAYING ? "pause" : "play"]();
  };
  onPlay = () => {
    if (this.state.playState !== PLAYING) {
      this.setState({
        playState: PLAYING
      });
    }
    this.hideControls(null, true);
  };
  onPause = () => {
    if (this.state.playState !== PAUSED) {
      this.setState({
        playState: PAUSED
      });
      this.displayControls();
    }
  };
  onEnded = () => {
    if (this.state.playState !== STOPPED) {
      this.setState({
        playState: STOPPED
      });
    }
    this.video.current.currentTime = 0;
  };
  displayControls = () => {
    clearTimeout(this.timeout);
    this.setState({ showControls: true });
    if (this.state.playState === PLAYING) {
      this.timeout = setTimeout(() => {
        this.setState({ showControls: false });
      }, 1000);
    }
  };
  hideControls = (event, force) => {
    clearTimeout(this.timeout);
    if (force === true || this.state.playState === PLAYING) {
      this.setState({ showControls: false });
    }
  };
  setMuted = muted => {
    if (!this.props.forceMuted && muted !== this.state.muted) {
      this.setState({
        muted
      });
    }
  };
  setVolume = volume => {
    volume = Number(volume);
    if (volume >= 0 && volume <= 100) {
      this.setState({
        volume,
        muted: false
      });
      if (this.video.current) {
        this.video.current.volume = volume / 100;
      }
    }
  };
}

Video.propTypes = {
  videos: propTypes.array.isRequired,
  controls: propTypes.bool,
  autoPlay: propTypes.bool,
  muted: propTypes.bool,
  forceMuted: propTypes.bool,
  volume: propTypes.number,
  playsInline: propTypes.bool,
  loop: propTypes.bool,
  classes: propTypes.object.isRequired,
  title: propTypes.element,
  screenTop: propTypes.oneOfType([propTypes.element, propTypes.func]),
  screenMiddle: propTypes.oneOfType([propTypes.element, propTypes.func]),
  screenBottom: propTypes.oneOfType([propTypes.element, propTypes.func]),
  onPlay: propTypes.func,
  onPause: propTypes.func,
  onEnded: propTypes.func
};

const defaultProps = {
  controls: true,
  autoPlay: false,
  muted: false,
  forceMuted: false,
  volume: 100,
  playsInline: false,
  loop: false,
  screenMiddle: video => {
    return (
      video.props.controls &&
      !video.props.nativeControls && (
        <PlayControl
          state={video.state.playState}
          togglePlay={video.togglePlay}
          classes={video.props.classes}
        />
      )
    );
  },
  screenBottom: video => {
    return (
      video.props.controls &&
      !video.props.nativeControls && (
        <VolumeControl
          forceMuted={video.props.forceMuted}
          muted={video.state.muted}
          setMuted={video.setMuted}
          volume={video.state.volume}
          setVolume={video.setVolume}
          classes={video.props.classes}
        />
      )
    );
  }
};
Video.defaultProps = defaultProps;

function VolumeControl(props) {
  const {
    forceMuted,
    classes = {},
    muted,
    volume,
    setMuted,
    setVolume
  } = props;
  if (forceMuted) return null;
  return (
    <div className={classes.Volume} onClick={event => event.stopPropagation()}>
      <button
        type="button"
        className={classNames(classes.Button, classes.MuteButton)}
        onClick={() => setMuted(!muted)}
      >
        {muted ? <MutedIcon /> : <UnmutedIcon />}
      </button>
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        value={volume}
        onChange={event => setVolume(event.target.value)}
      />
    </div>
  );
}
function PlayControl(props) {
  const { state, togglePlay, classes } = props;
  return (
    <button
      type="button"
      className={classNames(classes.Button, classes.TogglePlay)}
      onClick={togglePlay ? togglePlay : undefined}
    >
      {state === PLAYING ? (
        <PauseIcon className={classes.Icon} />
      ) : (
        <PlayIcon className={classes.Icon} />
      )}
    </button>
  );
}

function PlayIcon(props) {
  return (
    <svg
      height="60px"
      version="1.1"
      viewBox="0 0 60 60"
      width="60px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fillRule="evenodd"
        id="People"
        stroke="none"
        strokeWidth="1"
        fill="currentColor"
      >
        <path d="M40,30 C40,30.34 39.827,30.657 39.541,30.841 L29.531,37.276 C29.065,37.574 28.448,37.44 28.149,36.977 C27.851,36.512 27.985,35.894 28.449,35.595 L37.151,30 L26,22.832 L26,39 C26,39.553 25.552,40 25,40 C24.448,40 24,39.553 24,39 L24,21 C24,20.634 24.2,20.298 24.521,20.122 C24.841,19.947 25.232,19.959 25.541,20.159 L39.541,29.159 C39.827,29.343 40,29.66 40,30 M30,58 C14.561,58 2,45.439 2,30 C2,14.561 14.561,2 30,2 C45.439,2 58,14.561 58,30 C58,45.439 45.439,58 30,58 M30,0 C13.458,0 0,13.458 0,30 C0,46.542 13.458,60 30,60 C46.542,60 60,46.542 60,30 C60,13.458 46.542,0 30,0" />
      </g>
    </svg>
  );
}

function PauseIcon(props) {
  return (
    <svg
      height="60px"
      version="1.1"
      viewBox="0 0 60 60"
      width="60px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fillRule="evenodd"
        id="People"
        stroke="none"
        strokeWidth="1"
        fill="currentColor"
      >
        <path d="M28,21 L28,39 C28,39.553 27.552,40 27,40 C26.448,40 26,39.553 26,39 L26,22 L22,22 L22,38 L23,38 C23.552,38 24,38.447 24,39 C24,39.553 23.552,40 23,40 L21,40 C20.448,40 20,39.553 20,39 L20,21 C20,20.447 20.448,20 21,20 L27,20 C27.552,20 28,20.447 28,21 M40,21 L40,39 C40,39.553 39.552,40 39,40 C38.448,40 38,39.553 38,39 L38,22 L34,22 L34,38 L35,38 C35.552,38 36,38.447 36,39 C36,39.553 35.552,40 35,40 L33,40 C32.448,40 32,39.553 32,39 L32,21 C32,20.447 32.448,20 33,20 L39,20 C39.552,20 40,20.447 40,21 M30,58 C14.561,58 2,45.439 2,30 C2,14.561 14.561,2 30,2 C45.439,2 58,14.561 58,30 C58,45.439 45.439,58 30,58 M30,0 C13.458,0 0,13.458 0,30 C0,46.542 13.458,60 30,60 C46.542,60 60,46.542 60,30 C60,13.458 46.542,0 30,0" />
      </g>
    </svg>
  );
}

function UnmutedIcon(props) {
  return (
    <svg
      height="60px"
      version="1.1"
      viewBox="0 0 60 60"
      width="60px"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="currentColor"
        fillRule="evenodd"
        id="People"
        stroke="none"
        strokeWidth="1"
      >
        <path d="M37.6064,19.3936 C40.4394,22.2266 42.0004,25.9936 42.0004,29.9996 C42.0004,34.0066 40.4394,37.7736 37.6064,40.6066 C37.4114,40.8016 37.1554,40.8996 36.8994,40.8996 C36.6434,40.8996 36.3874,40.8016 36.1924,40.6066 C35.8014,40.2156 35.8014,39.5826 36.1924,39.1926 C38.6474,36.7376 40.0004,33.4726 40.0004,29.9996 C40.0004,26.5276 38.6474,23.2626 36.1924,20.8076 C35.8014,20.4166 35.8014,19.7846 36.1924,19.3936 C36.5834,19.0026 37.2154,19.0026 37.6064,19.3936 M36.0004,29.9996 C36.0004,32.4046 35.0644,34.6646 33.3644,36.3636 C33.1694,36.5586 32.9134,36.6566 32.6574,36.6566 C32.4014,36.6566 32.1454,36.5586 31.9504,36.3636 C31.5594,35.9726 31.5594,35.3396 31.9504,34.9496 C33.2724,33.6266 34.0004,31.8696 34.0004,29.9996 C34.0004,28.1306 33.2724,26.3726 31.9504,25.0506 C31.5594,24.6606 31.5594,24.0276 31.9504,23.6366 C32.3404,23.2466 32.9734,23.2466 33.3644,23.6366 C35.0644,25.3356 36.0004,27.5956 36.0004,29.9996 M30.0004,20.9996 L30.0004,38.9996 C30.0004,39.3696 29.7964,39.7076 29.4714,39.8816 C29.3244,39.9606 29.1624,39.9996 29.0004,39.9996 C28.8054,39.9996 28.6124,39.9436 28.4454,39.8316 L22.4454,35.8316 C21.9854,35.5256 21.8614,34.9056 22.1684,34.4456 C22.4724,33.9866 23.0924,33.8626 23.5544,34.1676 L28.0004,37.1316 L28.0004,22.8686 L23.5544,25.8316 C23.3904,25.9416 23.1974,25.9996 23.0004,25.9996 L20.0004,25.9996 L20.0004,34.9996 C20.0004,35.5526 19.5524,35.9996 19.0004,35.9996 C18.4474,35.9996 18.0004,35.5526 18.0004,34.9996 L18.0004,24.9996 C18.0004,24.4476 18.4474,23.9996 19.0004,23.9996 L22.6974,23.9996 L28.4454,20.1676 C28.7514,19.9636 29.1474,19.9446 29.4714,20.1186 C29.7964,20.2916 30.0004,20.6306 30.0004,20.9996 M30.0004,57.9996 C14.5604,57.9996 2.0004,45.4396 2.0004,29.9996 C2.0004,14.5606 14.5604,1.9996 30.0004,1.9996 C45.4394,1.9996 58.0004,14.5606 58.0004,29.9996 C58.0004,45.4396 45.4394,57.9996 30.0004,57.9996 M30.0004,-0.0004 C13.4584,-0.0004 0.0004,13.4576 0.0004,29.9996 C0.0004,46.5416 13.4584,59.9996 30.0004,59.9996 C46.5424,59.9996 60.0004,46.5416 60.0004,29.9996 C60.0004,13.4576 46.5424,-0.0004 30.0004,-0.0004" />
      </g>
    </svg>
  );
}

function MutedIcon(props) {
  return (
    <svg
      height="60px"
      version="1.1"
      viewBox="0 0 60 60"
      width="60px"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        fill="currentColor"
        fillRule="evenodd"
        id="People"
        stroke="none"
        strokeWidth="1"
      >
        <path d="M30,21 L30,39 C30,39.369 29.797,39.708 29.472,39.882 C29.324,39.961 29.162,40 29,40 C28.806,40 28.612,39.943 28.445,39.832 L22.445,35.832 C21.985,35.525 21.861,34.905 22.168,34.445 C22.474,33.986 23.094,33.862 23.555,34.168 L28,37.132 L28,22.868 L23.555,25.832 C23.391,25.941 23.197,26 23,26 L20,26 L20,35 C20,35.553 19.553,36 19,36 C18.447,36 18,35.553 18,35 L18,25 C18,24.447 18.447,24 19,24 L22.697,24 L28.445,20.168 C28.752,19.964 29.147,19.944 29.472,20.118 C29.797,20.292 30,20.631 30,21 M41.707,26.707 L38.414,30 L41.707,33.293 C42.098,33.684 42.098,34.316 41.707,34.707 C41.512,34.902 41.256,35 41,35 C40.744,35 40.488,34.902 40.293,34.707 L37,31.414 L33.707,34.707 C33.512,34.902 33.256,35 33,35 C32.744,35 32.488,34.902 32.293,34.707 C31.902,34.316 31.902,33.684 32.293,33.293 L35.586,30 L32.293,26.707 C31.902,26.316 31.902,25.684 32.293,25.293 C32.684,24.902 33.316,24.902 33.707,25.293 L37,28.586 L40.293,25.293 C40.684,24.902 41.316,24.902 41.707,25.293 C42.098,25.684 42.098,26.316 41.707,26.707 M30,58 C14.561,58 2,45.439 2,30 C2,14.561 14.561,2 30,2 C45.439,2 58,14.561 58,30 C58,45.439 45.439,58 30,58 M30,0 C13.458,0 0,13.458 0,30 C0,46.542 13.458,60 30,60 C46.542,60 60,46.542 60,30 C60,13.458 46.542,0 30,0" />
      </g>
    </svg>
  );
}

export default React.forwardRef((props, ref) => (
  <Video innerRef={ref} {...props} />
));
export {
  PlayControl,
  VolumeControl,
  PlayIcon,
  PauseIcon,
  UnmutedIcon,
  MutedIcon,
  defaultProps
};
